<template>
  <v-card
    variant="elevated"
    class="mx-auto bg-accent flex d-flex flex-column"
    style="border-radius: 25px"
    width="100%"
    height="100%"
  >
    <div>
      <v-img
        height="200px"
        max-height="200px"
        aspect-ratio="16/9"
        cover
        class="rounded-img position-relative"
        :src="
          item.information?.teaserImage
            ? item.information?.teaserImage
            : defaultImage
        "
      >
        <v-overlay
          contained
          :absolute="true"
          :model-value="!item.isPublic"
          class="align-start justify-start"
        >
          <v-tooltip location="bottom">
            <template v-slot:activator="{ props }">
              <v-icon
                v-if="!item.isPublic"
                class="ml-2"
                v-bind="props"
                color="white"
                size="75"
                >mdi-eye-off-outline</v-icon
              >
            </template>
            <span
              >Das Buchungsobjekt ist für die Öffentlichkeit nicht
              sichtbar</span
            >
          </v-tooltip>
        </v-overlay>
      </v-img>
    </div>
    <v-card-title>{{ item.information?.name }}</v-card-title>
    <v-card-text>
      <v-row no-gutters>
        <v-col cols="2" class="font-weight-bold">
          <small>Beginn:</small>
        </v-col>
        <v-col>
          {{ formatDate(item.information?.startDate) }} –
          {{ formatTime(item.information?.startTime) }} Uhr
        </v-col>
      </v-row>
      <v-row no-gutters>
        <v-col cols="2" class="font-weight-bold">
          <small>Ende:</small>
        </v-col>
        <v-col>
          {{ formatDate(item.information?.endDate) }} –
          {{ formatTime(item.information?.endTime) }} Uhr
        </v-col>
      </v-row>
      <v-row no-gutters>
        <v-col cols="2" class="font-weight-bold">
          <small>Ort:</small>
        </v-col>
        <v-col>
          {{ item.eventLocation?.name }}
        </v-col>
      </v-row>

      <p class="mt-2 mb-0" v-html="item.information?.teaserText"></p>
    </v-card-text>

    <v-card-text
      class="font-weight-bold text-h6 pb-0"
      v-if="item.attendees?.free"
    >
      Kostenfrei
    </v-card-text>
    <v-card-text
      class="font-weight-bold text-h6 pb-0"
      v-else-if="item.attendees?.priceCategories.price"
    >
      <!-- An neue Struktur anpassen, wenn klar -->
      {{ formatCurrency(item.attendees?.priceCategories.price) }}
    </v-card-text>

    <v-card-text
      class="font-weight-bold text-h6 pb-0"
      color="grey-darken-1"
      v-if="item.attendees?.maxAttendees"
      title="Maximale Teilnehmerzahl"
    >
      <v-icon class="pe-2" size="23">mdi-account</v-icon>
      <span class="text-subtitle-1">{{ item.attendees?.maxAttendees }}</span>
    </v-card-text>

    <v-card-text color="grey-darken-1" v-if="item.information?.flags">
      <ul class="unstyled-list">
        <li v-for="flag in item.information?.flags" :key="flag" class="d-flex">
          <v-icon class="pe-2" size="23">mdi-check</v-icon>
          <div class="text-subtitle-1">{{ flag }}</div>
        </li>
      </ul>
    </v-card-text>

    <v-card-actions class="py-4 mt-auto">
      <v-spacer></v-spacer>
      <v-btn
        class="bg-secondary"
        color="black"
        rounded
        variant="text"
        :to="{
          name: 'event-create-information',
          query: { id: item.id, fromRoute: fromRoute },
        }"
        :disabled="!BookablePermissionService.allowUpdate(item)"
      >
        Bearbeiten
      </v-btn>

      <v-menu>
        <template v-slot:activator="{ props }">
          <v-tooltip location="bottom">
            <template v-slot:activator="{ on: tooltip }">
              <v-btn icon density="comfortable" variant="text" v-bind="props">
                <v-icon size="25">mdi-dots-vertical</v-icon>
              </v-btn>
            </template>
            <span>Einstellungen</span>
          </v-tooltip>
        </template>
        <v-list density="compact">
          <v-list-item
            @click="copyEventId(item.id)"
            prepend-icon="mdi-clipboard-text-outline"
          >
            <v-list-item-title>Event-ID kopieren</v-list-item-title>
          </v-list-item>
          <v-divider></v-divider>
          <v-list-item
            link
            :href="eventBookingsDownloadLink(item.id, item.tenantId)"
            target="_blank"
            :disabled="!BookablePermissionService.allowUpdate(item)"
            prepend-icon="mdi-account-group"
          >
            <v-list-item-title>Teilnehmerliste herunterladen</v-list-item-title>
          </v-list-item>
          <v-divider></v-divider>
          <v-list-item
            link
            @click="emitDuplicateAction"
            :disabled="duplicateDisabled"
            prepend-icon="mdi-content-copy"
          >
            <v-list-item-title>Duplizieren</v-list-item-title>
          </v-list-item>
          <v-divider
            v-if="BookablePermissionService.allowDelete(item)"
          ></v-divider>
          <v-list-item
            class="text-red"
            link
            @click="emitDeleteAction"
            :disabled="!BookablePermissionService.allowDelete(item)"
            v-if="BookablePermissionService.allowDelete(item)"
            prepend-icon="mdi-delete"
          >
            <v-list-item-title>Löschen</v-list-item-title>
          </v-list-item>
        </v-list>
      </v-menu>
    </v-card-actions>
  </v-card>
</template>

<script>
import { mapActions } from "vuex";
import ApiEventService from "@/services/api/ApiEventService";
import BookablePermissionService from "@/services/permissions/BookablePermissionService";
import ToastService from "@/services/ToastService";
import FormatService from "@/services/FormatService";

export default {
  props: {
    fromRoute: String,
    item: {
      type: Object,
      required: true,
    },
  },
  data() {
    return {
      defaultImage: require("@/assets/bookable-default.jpg"),
      isDuplicateAllowed: true,
    };
  },
  computed: {
    BookablePermissionService() {
      return BookablePermissionService;
    },
    duplicateDisabled() {
      return (
        !this.BookablePermissionService.allowCreate() ||
        !this.isDuplicateAllowed
      );
    },
  },
  methods: {
    ...mapActions({
      addToast: "toasts/add",
    }),
    async copyEventId(id) {
      try {
        await navigator.clipboard.writeText(id);
        this.addToast(
          ToastService.createToast(
            "event.copyToClipBoard.success",
            "success",
            2000
          )
        );
      } catch (err) {
        console.error(err);
        this.addToast(
          ToastService.createToast(
            "event.copyToClipBoard.error",
            "success",
            2000
          )
        );
      }
    },
    formatDate(date) {
      if (!_.isNil(date)) {
        return FormatService.date(date);
      }
      return date;
    },
    formatTime(time) {
      if (!time) return null;
      return FormatService.time(time);
    },
    formatCurrency(amount) {
      if (!amount) return null;
      return FormatService.currency(amount, "EUR", "de-DE");
    },
    emitDeleteAction() {
      this.$emit("delete");
    },
    emitDuplicateAction() {
      this.$emit("duplicate");
    },
    eventBookingsDownloadLink(id, tenantId) {
      return `${process.env.VUE_APP_SERVER_BASE_URL}/csv/${tenantId}/events/${id}/bookings`;
    },
    async setAllowDuplicate() {
      const eventCountCheck = await ApiEventService.publicEventCountCheck();
      this.isDuplicateAllowed = eventCountCheck || !this.item.isPublic;
    },
  },
  created() {},
  mounted() {
    this.setAllowDuplicate();
  },
};
</script>

<style scoped lang="scss">
.unstyled-list {
  list-style: none;
  padding: 0;
  margin: 0;
}
.rounded-img {
  border-top-left-radius: 25px !important;
  border-top-right-radius: 25px !important;
  height: 200px;
  max-height: 200px;
}
</style>
